// ViewportDropZone — drag an environment from the assets dock into the scene.
//
// The content browser already made its cards draggable and folders already
// accepted them; the viewport was the one target where the obvious DCC gesture
// did nothing. A drop here runs the SAME applyEnvironment() the card's context
// menu runs ("Apply environment"), so there is one code path: same splatUrl +
// saved default_pose write, same shared-tune application, same auto-flip into
// live view.
//
// Wraps the viewport slot rather than living inside it: the Canvas owns pointer
// events for the camera, and an overlay that swallowed dragover would fight it.
// This div sits around the whole slot, is invisible until a drag with an asset
// payload enters, and only then paints a hint.
//
// Feedback is mandatory, not decorative. A drop that can't be applied — the
// asset was deleted mid-drag, or it isn't an environment at all — says so in a
// transient pill. A silent no-op would read as a broken drop target, which is
// exactly the bug this feature is meant to remove.

import React, { useCallback, useRef, useState } from 'react'

import { assetRegistry } from '../lib/assetRegistry'
import { applyEnvironment } from '../lib/applyEnvironment'
import { isAssetDrag, resolveDroppedEnvironment, ASSET_DRAG_TYPE } from '../lib/viewportDrop.js'
import { UI_FONT } from './PipelineGlyph.jsx'

const TOAST_MS = { ok: 2200, error: 4200 }

export default function ViewportDropZone({ children }) {
  const [dragging, setDragging] = useState(false)
  const [toast, setToast] = useState(null) // { phase: 'ok'|'error', text }
  // dragenter/dragleave fire for every child element the pointer crosses, so a
  // bare boolean flickers as the cursor moves over the canvas. Counting entries
  // against leaves is the standard fix.
  const depth = useRef(0)
  const toastTimer = useRef(null)

  const say = useCallback((phase, text) => {
    setToast({ phase, text })
    if (toastTimer.current) clearTimeout(toastTimer.current)
    toastTimer.current = setTimeout(() => setToast(null), TOAST_MS[phase] ?? 2500)
  }, [])

  const onDragEnter = useCallback((e) => {
    if (!isAssetDrag(e.dataTransfer?.types)) return
    depth.current += 1
    setDragging(true)
  }, [])

  const onDragLeave = useCallback((e) => {
    if (!isAssetDrag(e.dataTransfer?.types)) return
    depth.current = Math.max(0, depth.current - 1)
    if (depth.current === 0) setDragging(false)
  }, [])

  const onDragOver = useCallback((e) => {
    if (!isAssetDrag(e.dataTransfer?.types)) return
    // preventDefault is what makes this a drop target at all; without it the
    // browser refuses the drop and shows the not-allowed cursor.
    e.preventDefault()
    // 'copy', matching AssetCard's effectAllowed='copyMove' — applying an
    // environment references the asset, it doesn't move it out of its folder.
    e.dataTransfer.dropEffect = 'copy'
  }, [])

  const onDrop = useCallback((e) => {
    if (!isAssetDrag(e.dataTransfer?.types)) return
    e.preventDefault()
    depth.current = 0
    setDragging(false)

    const id = e.dataTransfer.getData(ASSET_DRAG_TYPE)
    const result = resolveDroppedEnvironment(id, assetRegistry.list())
    if (!result.ok) {
      say('error', result.message)
      return
    }
    const url = applyEnvironment(result.asset)
    if (!url) {
      // applyEnvironment already warned; surface it rather than looking inert.
      say('error', `Could not apply ${result.asset.name} — no file URL`)
      return
    }
    say('ok', `Applied ${result.asset.name} as environment`)
  }, [say])

  return (
    <div
      data-testid="viewport-drop-zone"
      data-dragging={dragging ? '1' : '0'}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
      style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' }}
    >
      {children}

      {/* Hint. pointerEvents none so it can never intercept the drop it is
          advertising, and it only exists mid-drag. */}
      {dragging && (
        <div
          data-testid="viewport-drop-hint"
          style={{
            position: 'absolute', inset: 0, zIndex: 25, pointerEvents: 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '2px dashed var(--m-accent)',
            background: 'color-mix(in srgb, var(--m-accent) 8%, transparent)',
          }}
        >
          <span style={{
            fontFamily: UI_FONT, fontSize: 11, letterSpacing: '.08em',
            padding: '7px 14px', borderRadius: 6,
            background: 'color-mix(in srgb, var(--m-panel) 92%, transparent)',
            border: '1px solid var(--m-accent)', color: 'var(--m-fg)',
          }}>Drop to apply as environment</span>
        </div>
      )}

      {toast && (
        <div
          data-testid="viewport-drop-toast"
          data-phase={toast.phase}
          style={{
            position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
            zIndex: 30, pointerEvents: 'none',
            fontFamily: UI_FONT, fontSize: 11,
            padding: '6px 12px', borderRadius: 6, maxWidth: '80%', textAlign: 'center',
            background: 'color-mix(in srgb, var(--m-panel) 92%, transparent)',
            border: `1px solid ${toast.phase === 'ok' ? 'var(--m-online)' : 'var(--m-danger)'}`,
            color: toast.phase === 'ok' ? 'var(--m-online)' : 'var(--m-danger)',
          }}
        >{toast.text}</div>
      )}
    </div>
  )
}
